import { useEffect, useState, useCallback } from 'react';
import { CheckIcon, XIcon, HelpCircleIcon, AlertCircleIcon, InboxIcon } from 'lucide-react';
import type {
  AnswerFeedbackLabel,
  ChangeRequest,
  ChangeRequestStatus,
  ChangeRequestType,
  MilaBeoordelaarRij,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiGet, apiPost } from '../api/client.js';

/**
 * Beheer ⟶ Wijzigingsverzoeken — Linda's review queue for the feedback
 * funnel (answer-feedback, kennisbank-aanvullingen, sociale-kaart, fouten).
 * Replaces the mailto-to-Linda flow. RBAC: suppohrt_admin only, gated both
 * at the route (RoleGuard) and server-side.
 */

const TYPE_LABEL: Record<ChangeRequestType, string> = {
  kennisbank_aanvulling: 'Kennisbank-aanvulling',
  sociale_kaart_update: 'Sociale Kaart',
  fout_melden: 'Fout melden',
  antwoord_feedback: 'Antwoord-feedback',
  overig: 'Overig',
};

const STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  new: 'Nieuw',
  in_progress: 'In behandeling',
  info_requested: 'Info gevraagd',
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
};

const STATUS_CHIP: Record<ChangeRequestStatus, string> = {
  new: 'bg-petrol-50 text-petrol-700',
  in_progress: 'bg-amber-100 text-amber-800',
  info_requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
};

const ANSWER_LABEL: Record<AnswerFeedbackLabel, string> = {
  feitelijk_onjuist: 'Klopt niet / feitelijk onjuist',
  bron_probleem: 'Verkeerde of ontbrekende bron',
  onvolledig: 'Onvolledig / mist nuance',
  verkeerde_toon: 'Verkeerde toon of te lang',
  kennis_ontbreekt: 'Kennis ontbreekt in de kennisbank',
};

interface RatingStats {
  total: number;
  average: number | null;
  distribution: Record<string, number>;
  weakSources: { source: string; count: number; average: number }[];
  lowRated: number;
  truncated: boolean;
  periodDays: number;
}

/** Perioden die het paneel aanbiedt; de backend accepteert exact deze waarden. */
const RATING_PERIODS: { days: number; label: string }[] = [
  { days: 7, label: 'Afgelopen week' },
  { days: 30, label: 'Afgelopen maand' },
  { days: 90, label: 'Afgelopen kwartaal' },
  { days: 365, label: 'Afgelopen jaar' },
  { days: 1825, label: 'Alles' },
];

/**
 * Meetkant van de leerlus (ADR 0016): hoe beoordelen gebruikers Mila, en welke
 * bronnen zitten structureel onder een lage beoordeling? Laag beoordeelde
 * antwoorden worden regressievragen via
 * agents/kennisagent/scripts/feedback-to-evals.py.
 */
function RatingPanel(): JSX.Element {
  const [days, setDays] = useState(90);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [failed, setFailed] = useState(false);
  // Zodra de beheerder zelf een periode kiest blijft het paneel staan, ook als
  // die periode leeg is. Anders verdwijnt met de cijfers ook de keuzelijst en
  // kan hij niet terug naar een periode die wél data heeft.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet<RatingStats>('/api/v1/change-requests/ratings', { days: String(days) })
      .then((next) => {
        if (!cancelled) setStats(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  // Onderscheid tussen "kon niet laden" en "nog niets beoordeeld": allebei een
  // leeg paneel tonen laat een kapot endpoint er rustig uitzien.
  if (failed) {
    return <p className="mb-4 text-sm text-red-700">Beoordelingen konden niet geladen worden.</p>;
  }
  if (!stats) return <></>;
  if (stats.total === 0 && !touched) {
    return (
      <p className="mb-4 text-sm text-ink-900/60">Nog geen beoordelingen van Mila-antwoorden.</p>
    );
  }
  const max = Math.max(...[1, 2, 3, 4, 5].map((n) => stats.distribution[String(n)] ?? 0), 1);
  const periodLabel = (
    RATING_PERIODS.find((p) => p.days === stats.periodDays)?.label ?? `${stats.periodDays} dagen`
  ).toLowerCase();

  return (
    <section className="mb-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-semibold text-ink-900">Beoordeling van Mila</h2>
          {stats.total > 0 && (
            <span className="text-sm text-ink-900/65">
              gemiddeld <strong className="text-ink-900">{stats.average}</strong> van 5 over{' '}
              {stats.total} beoordelingen ({periodLabel}) — {stats.lowRated} met 1 of 2 sterren
            </span>
          )}
        </div>
        <label className="flex items-center gap-2 text-[12px] text-ink-900/60">
          Periode
          <select
            value={days}
            onChange={(e) => {
              setTouched(true);
              setDays(Number(e.target.value));
            }}
            className="rounded-md border border-ring2 bg-white px-2 py-1 text-[12px] text-ink-900"
          >
            {RATING_PERIODS.map((p) => (
              <option key={p.days} value={p.days}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {stats.total === 0 ? (
        <p className="mt-3 text-sm text-ink-900/60">
          Geen beoordelingen in deze periode. Kies een langere periode om verder terug te kijken.
        </p>
      ) : (
        <>
          {stats.truncated && (
            <p className="mt-2 text-[12px] text-amber-800">
              De scan raakte zijn limiet: oudere beoordelingen tellen niet mee. Kies een kortere
              periode, of zet de statistiek op een Firestore-query met index.
            </p>
          )}

          <div className="mt-3 space-y-1">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = stats.distribution[String(n)] ?? 0;
              return (
                <div key={n} className="flex items-center gap-2 text-[12px]">
                  <span className="w-10 text-ink-900/60">{n} ★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink-900/5">
                    <div
                      className="h-full rounded-full bg-mosterdgeel-500"
                      style={{ width: `${(count / max) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-ink-900/60">{count}</span>
                </div>
              );
            })}
          </div>

          {stats.weakSources.length > 0 && (
            <div className="mt-3 border-t border-ring2/70 pt-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-900/60">
                Bronnen onder lage beoordelingen
              </p>
              <ul className="space-y-0.5 text-[12px] text-ink-900/75">
                {stats.weakSources.map((w) => (
                  <li key={w.source}>
                    {w.source} — gemiddeld {w.average} over {w.count} antwoorden
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Wie praat er met Mila en beoordeelt niet (issue #237)? Het overzicht staat
 * naast de beoordelingscijfers omdat het dezelfde vraag beantwoordt: hoe komt
 * er meer en beter materiaal in de leerlus. Bewust alleen aantallen en datums,
 * en bewust geen knop die mensen een herinnering stuurt: dit is het gereedschap
 * voor een persoonlijk gesprek.
 */
function BeoordelaarsPanel(): JSX.Element {
  const [data, setData] = useState<{ rijen: MilaBeoordelaarRij[]; truncated: boolean } | null>(
    null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ rijen: MilaBeoordelaarRij[]; truncated: boolean }>(
      '/api/v1/change-requests/beoordelaars',
    )
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <p className="mb-4 text-sm text-red-700">
        Het overzicht van beoordelaars kon niet geladen worden.
      </p>
    );
  }
  if (!data) return <></>;

  return (
    <section className="mb-4 rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <h2 className="font-semibold text-ink-900">Wie beoordeelt Mila?</h2>
      <p className="mt-1 text-sm text-ink-900/65">
        Bovenaan staan de collega&apos;s die veel met Mila praten en weinig beoordelen. Spreek ze
        zelf aan op hun eigen vakgebied; de app stuurt hier expres geen herinneringen. Gesprekken
        tellen over de laatste 30 dagen, want daarna worden chats verwijderd.
      </p>

      {data.rijen.length === 0 ? (
        <p className="mt-3 text-sm text-ink-900/60">
          Nog niemand heeft de AI-tools aanstaan, dus er valt nog niets te tellen.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[30rem] text-left text-[13px]">
            <thead className="text-[11px] uppercase tracking-wider text-ink-900/55">
              <tr>
                <th className="py-1 pr-3 font-semibold">Naam</th>
                <th className="py-1 pr-3 text-right font-semibold">Gesprekken</th>
                <th className="py-1 pr-3 text-right font-semibold">Beoordelingen</th>
                <th className="py-1 font-semibold">Laatste beoordeling</th>
              </tr>
            </thead>
            <tbody className="text-ink-900/80">
              {data.rijen.map((r) => (
                <tr key={r.userId} className="border-t border-ring2/60">
                  <td className="py-1.5 pr-3 text-ink-900">{r.displayName}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.gesprekken}</td>
                  <td
                    className={`py-1.5 pr-3 text-right tabular-nums ${
                      r.beoordelingen === 0 && r.gesprekken > 0 ? 'font-semibold text-ring1-fg' : ''
                    }`}
                  >
                    {r.beoordelingen}
                  </td>
                  <td className="py-1.5 text-ink-900/60">
                    {r.laatsteBeoordelingAt
                      ? new Date(r.laatsteBeoordelingAt).toLocaleDateString('nl-NL')
                      : 'nog nooit'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.truncated && (
        <p className="mt-2 text-[12px] text-amber-800">
          De scan raakte zijn limiet: heel oude beoordelingen tellen niet mee in deze aantallen.
        </p>
      )}
    </section>
  );
}

export function BeheerWijzigingsverzoekenPage(): JSX.Element {
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ChangeRequestType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ChangeRequestStatus | ''>('new');

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const resp = await apiGet<{ requests: ChangeRequest[] }>('/api/v1/change-requests', {
        ...(typeFilter ? { type: typeFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      setRequests(resp.requests);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? `Laden mislukt (${err.status}): ${err.message}`
          : 'Wachtrij kon niet geladen worden.',
      );
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-ink-900">Beheer Wijzigingsverzoeken</h1>
          <p className="mt-1 text-sm text-ink-900/65">
            Meldingen van gebruikers over antwoorden, documenten en de Sociale Kaart.
            Antwoord-feedback met het label “Kennis ontbreekt” wijst op een bron die aan de
            Kennisbank toegevoegd kan worden.
          </p>
        </header>

        <RatingPanel />
        <BeoordelaarsPanel />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ChangeRequestStatus | '')}
            className="rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900"
          >
            <option value="">Alle statussen</option>
            {(Object.keys(STATUS_LABEL) as ChangeRequestStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ChangeRequestType | '')}
            className="rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900"
          >
            <option value="">Alle types</option>
            {(Object.keys(TYPE_LABEL) as ChangeRequestType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        {errorMsg && (
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircleIcon size={14} /> {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
            Laden…
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-ring2 bg-white px-6 py-12 text-center text-sm text-ink-900/55">
            <InboxIcon size={28} className="text-ink-900/30" />
            Geen verzoeken in deze weergave.
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {requests.map((r) => (
              <RequestCard key={r.id} req={r} onReviewed={load} />
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

function RequestCard({
  req,
  onReviewed,
}: {
  req: ChangeRequest;
  onReviewed: () => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isOpen =
    req.status === 'new' || req.status === 'in_progress' || req.status === 'info_requested';

  async function review(action: 'approve' | 'reject' | 'request_info'): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await apiPost(`/api/v1/change-requests/${req.id}/review`, {
        action,
        ...(note.trim() ? { reviewerNote: note.trim() } : {}),
      });
      onReviewed();
    } catch (e) {
      setErr(e instanceof ApiError ? `Mislukt (${e.status})` : 'Mislukt');
      setBusy(false);
    }
  }

  return (
    <li className="rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-full bg-ink-900/5 px-2 py-0.5 font-medium text-ink-900/70">
              {TYPE_LABEL[req.type]}
            </span>
            <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_CHIP[req.status]}`}>
              {STATUS_LABEL[req.status]}
            </span>
            {req.answerRating !== undefined && req.answerRating >= 1 && req.answerRating <= 5 && (
              <span className="rounded-full bg-mosterdgeel-10 px-2 py-0.5 font-medium text-mosterdgeel-600">
                {'★'.repeat(req.answerRating)}
                {'☆'.repeat(5 - req.answerRating)}
              </span>
            )}
            {req.answerFeedbackLabel && (
              <span className="rounded-full bg-ring1-bg px-2 py-0.5 font-medium text-ring1-fg">
                {ANSWER_LABEL[req.answerFeedbackLabel]}
              </span>
            )}
            <span className="text-ink-900/45">
              {new Date(req.createdAt).toLocaleString('nl-NL')}
            </span>
          </div>
          <p className="font-semibold text-ink-900">{req.title}</p>
        </div>
      </div>

      {req.priorMessages && req.priorMessages.length > 0 && (
        <details className="mt-2 rounded-md bg-ink-900/[0.03] px-3 py-1.5 text-[12px] text-ink-900/70">
          <summary className="cursor-pointer font-medium text-ink-900/55">
            Eerdere berichten in dit gesprek ({req.priorMessages.length})
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {req.priorMessages.map((m, i) => (
              <li key={i} className="whitespace-pre-wrap">
                <span className="font-medium">{m.role === 'user' ? 'Vraag' : 'Mila'}:</span>{' '}
                {m.content}
              </li>
            ))}
          </ul>
        </details>
      )}
      {req.originatingQuery && (
        <p className="mt-2 text-sm text-ink-900/70">
          <span className="font-medium text-ink-900/55">Vraag:</span> {req.originatingQuery}
        </p>
      )}
      {/* De vraag en de voorafgaande berichten van een beoordeling komen niet
          mee over de API (#269): bij een gesprek dat uit Sara komt is de vraag
          de SOEP van een consult. Zonder deze regel lijkt het scherm stuk. */}
      {req.type === 'antwoord_feedback' && !req.originatingQuery && (
        <p className="mt-2 text-[12px] text-ink-900/45">
          De vraag en het gespreksverloop worden hier niet getoond. Die kunnen consultinhoud
          bevatten en zijn alleen machinaal beschikbaar voor de kwaliteitsmeting.
        </p>
      )}
      <p className="mt-1 whitespace-pre-wrap text-sm text-ink-900/80">{req.description}</p>

      {req.citedSources && req.citedSources.length > 0 && (
        <div className="mt-2 text-[12px] text-ink-900/60">
          <span className="font-medium">Geciteerde bronnen:</span>
          <ul className="ml-4 list-disc">
            {req.citedSources.map((c, i) => (
              <li key={i}>{c.title || c.filename || c.chunkId}</li>
            ))}
          </ul>
        </div>
      )}

      {req.reviewerNote && (
        <p className="mt-2 rounded-md bg-ink-900/5 px-3 py-1.5 text-[12px] text-ink-900/70">
          <span className="font-medium">Notitie beheerder:</span> {req.reviewerNote}
        </p>
      )}

      {isOpen && (
        <div className="mt-3 border-t border-ring2/70 pt-3">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notitie, of vraag aan de indiener…"
            className="mb-2 w-full rounded-md border border-ring2 px-3 py-1.5 text-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => review('approve')}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckIcon size={14} /> Goedkeuren
            </button>
            <button
              type="button"
              disabled={busy || !note.trim()}
              onClick={() => review('request_info')}
              title={note.trim() ? 'Stuurt de indiener een bericht' : 'Typ eerst een vraag'}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
            >
              <HelpCircleIcon size={14} /> Info vragen
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => review('reject')}
              className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              <XIcon size={14} /> Afwijzen
            </button>
            {err && <span className="text-[12px] text-red-700">{err}</span>}
          </div>
        </div>
      )}
    </li>
  );
}
